import { useNavigate } from "react-router-dom";
import React, { useState } from "react";
import api from "../api/axiosInstance";

function AdminConsumerExcelUpload() {
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const [uploadInfo, setUploadInfo] = useState(null);
  const [stockSummary, setStockSummary] = useState(null);
  const [salesSummary, setSalesSummary] = useState(null);
  const [cleanInfo, setCleanInfo] = useState(null);

  // ---------------- HELPERS ----------------
  const formatMoney = (v) => {
    if (!v) return "₹0.00";
    try {
      return v.toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
      });
    } catch {
      return `₹${v}`;
    }
  };

  const formatNumber = (v) => (v ? v.toLocaleString("en-IN") : "0");

  // ---------------- HANDLERS ----------------
  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setMessage("");
    setUploadInfo(null);
    setStockSummary(null);
    setSalesSummary(null);
    setCleanInfo(null);
  };

  const handleUpload = async () => {
    if (!file) return setMessage("Please select an Excel file.");

    setIsUploading(true);
    setMessage("Uploading...");
    setUploadInfo(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("consumer/upload-excel/", formData);
      setUploadInfo(res.data);
      setMessage("Excel uploaded successfully ✅");
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.error || "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleViewStockSummary = async () => {
    setMessage("");
    try {
      const res = await api.get("consumer/stock-summary/");
      setStockSummary(res.data);
      setMessage("Stock summary loaded ✅");
    } catch (err) {
      console.error(err);
      setMessage("Failed to load stock summary");
    }
  };

  const handleViewSalesSummary = async () => {
    setMessage("");
    try {
      const res = await api.get("consumer/sales-summary/");
      setSalesSummary(res.data);
      setMessage("Sales summary loaded ✅");
    } catch (err) {
      console.error(err);
      setMessage("Failed to load sales summary");
    }
  };

  const handleCleanDuplicates = async () => {
    if (!window.confirm("Clean duplicate products?")) return;

    try {
      const res = await api.post("consumer/clean-duplicates/");
      setCleanInfo(res.data);
      setMessage("Duplicate cleaning completed ✅");
    } catch (err) {
      console.error(err);
      setMessage("Failed to clean duplicates");
    }
  };

  const handleDownloadStock = async () => {
    try {
      const res = await api.get("consumer/download-stock/", {
        responseType: "blob",
      });

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "consumer_products_stock.xlsx";
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setMessage("Failed to download stock");
    }
  };

  const handleResetSales = async () => {
    if (!window.confirm("Are you sure? This will reset ALL consumer sales.")) return;

    try {
      await api.post("consumer/reset-sales/");
      alert("All consumer sales reset ✔");
    } catch (err) {
      console.error(err);
      alert("Failed to reset sales ❌");
    }
  };

  const handleBack = () => navigate(-1);

  // ---------------- UI ----------------
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={styles.headerIcon}>🛍️</span>
            <h2 style={styles.title}>Consumer Products Excel</h2>
          </div>
          <button onClick={handleBack} style={styles.backBtn}>BACK</button>
        </div>

        <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} />
        {file && <p style={styles.small}>Selected: {file.name}</p>}

        <div style={styles.buttonRow}>
          <button onClick={handleUpload} disabled={!file || isUploading} style={styles.primaryBtn}>
            ⬆ Upload Excel
          </button>

          <button onClick={handleViewStockSummary} style={styles.secondaryBtn}>
            📦 Stock Summary
          </button>

          <button onClick={handleViewSalesSummary} style={styles.secondaryBtn}>
            💰 Sales Summary
          </button>

          <button onClick={handleCleanDuplicates} style={styles.secondaryBtn}>
            🧹 Clean Duplicates
          </button>

          <button onClick={handleDownloadStock} style={styles.secondaryBtn}>
            ⬇ Download Stock
          </button>

          <button onClick={handleResetSales} style={styles.dangerBtn}>
            🔄 Reset All Sales
          </button>
        </div>

        {message && <p style={styles.message}>{message}</p>}

        {uploadInfo && (
          <div style={styles.infoBox}>
            <h4>Upload Summary</h4>
            <p>Created: {formatNumber(uploadInfo.created)}</p>
            <p>Updated: {formatNumber(uploadInfo.updated)}</p>
            <p>Rejected: {formatNumber(uploadInfo.rejected)}</p>
            <p>Total Rows: {formatNumber(uploadInfo.total_rows)}</p>
          </div>
        )}

        {stockSummary && (
          <div style={styles.infoBox}>
            <h4>Stock Summary</h4>
            <p>Total Products: {stockSummary.total_items}</p>
            <p>Total Stock Units: {stockSummary.total_stock_units}</p>
            <p>Total Purchase Value: {formatMoney(stockSummary.total_purchase_value)}</p>
            <p>Total Sale Value: {formatMoney(stockSummary.total_sale_value)}</p>
            <p>
              Profit: {formatMoney(stockSummary.total_profit_value)} (
              {stockSummary.total_profit_percent.toFixed(2)}%)
            </p>
          </div>
        )}

        {salesSummary && (
          <div style={styles.infoBox}>
            <h4>Sales Summary</h4>
            <p>Total Sale Done: {formatMoney(salesSummary.total_sale_done_till_now)}</p>
          </div>
        )}

        {cleanInfo && (
          <div style={styles.infoBox}>
            <h4>Duplicate Cleaning</h4>
            <p>Groups: {cleanInfo.duplicate_groups}</p>
            <p>Removed: {cleanInfo.records_removed}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- STYLES ----------------
const styles = {
  page: {
    background: "#f4f5fb",
    minHeight: "100vh",
    padding: 40,
    display: "flex",
    justifyContent: "center",
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    width: "100%",
    maxWidth: 1000,
    padding: 24,
    boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  headerIcon: { fontSize: 28 },
  title: { fontSize: 22, fontWeight: "bold" },
  backBtn: {
    background: "#000",
    color: "#fff",
    borderRadius: 6,
    padding: "8px 14px",
    cursor: "pointer",
  },
  buttonRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 15,
  },
  primaryBtn: {
    background: "#2a7ae4",
    color: "#fff",
    border: "none",
    padding: "10px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: "bold",
  },
  secondaryBtn: {
    background: "#fff",
    border: "1px solid #999",
    padding: "10px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: "bold",
  },
  dangerBtn: {
    background: "#d32f2f",
    color: "#fff",
    border: "none",
    padding: "10px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: "bold",
  },
  message: { marginTop: 12, fontWeight: "bold" },
  infoBox: {
    background: "#eef3ff",
    padding: 14,
    marginTop: 15,
    borderRadius: 10,
    border: "1px solid #cdd9ff",
  },
  small: { fontSize: 12, color: "#555" },
};

export default AdminConsumerExcelUpload;
